import {StyleSheet} from "aphrodite";

import {addStyle} from "@khanacademy/wonder-blocks-core";
import {font, sizing} from "@khanacademy/wonder-blocks-tokens";

import type {JsonPath} from "../../types.ts";
import {isNotPlaceholder, isPlaceholder} from "../../utils.ts";
import EditableItem from "../editable-item.tsx";
import PlaceholderBlock from "../placeholder-block.tsx";

import ListNode from "./list-node.tsx";
import ParagraphNode from "./paragraph-node.tsx";
import SubsectionNode from "./subsection-node.tsx";
import type {SectionData} from "./types.ts";

const StyledSection = addStyle("section");
const StyledH2 = addStyle("h2");

export default function SectionNode(props: {
    heading: SectionData["heading"];
    content: SectionData["content"];
    path: JsonPath;
}) {
    const {heading, content, path} = props;

    return (
        <EditableItem style={styles.section} path={path} tag={StyledSection}>
            {heading && <StyledH2 style={styles.h2}>{heading}</StyledH2>}

            {isPlaceholder(content) && (
                <PlaceholderBlock placeholderKey={content.$placeholder} />
            )}

            {isNotPlaceholder(content) &&
                content?.map((content, c) => {
                    if (isPlaceholder(content)) {
                        return (
                            <PlaceholderBlock
                                key={c}
                                placeholderKey={content.$placeholder}
                            />
                        );
                    }

                    const contentPath = [...path, "content", `${c}`];

                    if (content.kind === "subsection") {
                        return (
                            <SubsectionNode
                                key={c}
                                heading={content.heading}
                                content={content.content}
                                path={contentPath}
                            />
                        );
                    }

                    if (content.kind === "paragraph") {
                        return (
                            <ParagraphNode
                                key={c}
                                text={content.text}
                                path={contentPath}
                            />
                        );
                    }

                    if (
                        content.kind === "bulleted-list" ||
                        content.kind === "numbered-list"
                    ) {
                        return (
                            <ListNode
                                key={c}
                                kind={content.kind}
                                items={content.items}
                                path={contentPath}
                            />
                        );
                    }

                    return null;
                })}
        </EditableItem>
    );
}

const styles = StyleSheet.create({
    section: {
        display: "flex",
        flexDirection: "column",
        gap: sizing.size_160,
    },
    h2: {
        margin: 0,
        fontSize: font.size.large,
    },
});
